import { ShipmentDetails, ShippingPickupTypeEnum } from '@interfaces/models/timeline';
import { Trans } from 'next-i18next';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { use } from 'react';
import { StepInfoContext } from '@context/step-info.context';
import styles from './shipping.module.scss';

const langPrefix = 'MY_ACCOUNT.ORDERS_SALES.ACTION.GET_SHIPPING_LABEL.';
const classNamePath = 'shippingInfo';

interface ShippingInstructionDescriptionProps {
  shipmentDetails?: ShipmentDetails;
  carrierWebsite?: string;
  onOpenDropOffModal: () => void;
}

const ShippingInstructionDescription = ({
  shipmentDetails,
  carrierWebsite,
  onOpenDropOffModal,
}: ShippingInstructionDescriptionProps) => {
  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const { userType } = use(StepInfoContext);

  const { homeCollectionLink, homeCollectionPhoneNumber, dropOffMapAvailable, shippingOption } = shipmentDetails || {};

  const openDropOffModal = () => {
    sendAnalyticEvent('timeline_hyperlink', {
      action: 'clickable_text_drop_off_points_map_available',
      label: userType,
      property: 'shipping_info',
    });
    onOpenDropOffModal();
  };

  const openCarrierWebsite = () => {
    sendAnalyticEvent('timeline_hyperlink', {
      action: 'clickable_text_carrier_website',
      label: userType,
      property: 'shipping_info',
    });
    window.open(carrierWebsite, '_blank');
  };

  const clickDropOff = () => {
    if (dropOffMapAvailable) {
      openDropOffModal();
    } else {
      openCarrierWebsite();
    }
  };

  const openHomeCollectionLink = () => {
    sendAnalyticEvent('timeline_hyperlink', {
      action: homeCollectionLink,
      label: userType,
      property: 'shipping_info',
    });
    window.open(homeCollectionLink, '_blank');
  };

  // Drop-off description
  let dropOffDescription = `${langPrefix}SEE_NEAREST`;

  if (dropOffMapAvailable && carrierWebsite) {
    dropOffDescription = `${langPrefix}SEE_NEAREST_ONLINE`;
  }

  switch (shippingOption) {
    case ShippingPickupTypeEnum.dropOff: {
      if (!dropOffMapAvailable && !carrierWebsite) {
        return <></>;
      }

      return (
        <Trans
          i18nKey={dropOffDescription}
          ns="orders-sales"
          components={{
            clickable: (
              <button
                className={styles[`${classNamePath}__viewDropOff--link`]}
                onClick={clickDropOff}
              >
                {''}
              </button>
            ),
            a: carrierWebsite ? (
              <button
                className={styles[`${classNamePath}__viewDropOff--link`]}
                onClick={openCarrierWebsite}
              >
                {''}
              </button>
            ) : (
              <></>
            ),
          }}
        />
      );
    }

    case ShippingPickupTypeEnum.homeCollection: {
      let descKey = 'HOME-LINK-PHONE';

      if (homeCollectionLink && !homeCollectionPhoneNumber) {
        descKey = 'HOME-LINK';
      } else if (!homeCollectionLink && homeCollectionPhoneNumber) {
        descKey = 'HOME-PHONE';
      } else if (!homeCollectionLink && !homeCollectionPhoneNumber) {
        descKey = 'HOME-NO-LINK-PHONE';
      }

      return (
        <div className={styles[`${classNamePath}__viewDropOff--instruction`]}>
          <Trans
            i18nKey={`${langPrefix}${descKey}`}
            ns="orders-sales"
            values={{
              phone: homeCollectionPhoneNumber || '',
            }}
            components={{
              a: (
                <button
                  className={styles[`${classNamePath}__viewDropOff--link`]}
                  onClick={openHomeCollectionLink}
                >
                  {''}
                </button>
              ),
            }}
          />
        </div>
      );
    }

    default:
      return <></>;
  }
};

export default ShippingInstructionDescription;
